// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2, GraduationCap, Upload, X } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface CoachAttendanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventName: string;
  attendanceFee: number; // in cents
  couponCode?: string; // Event-specific coupon code
  /** Instructions to display at the top of the form */
  instructions?: string;
  onSuccess?: () => void;
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const CoachAttendanceModal: React.FC<CoachAttendanceModalProps> = ({
  open,
  onOpenChange,
  eventId,
  eventName,
  attendanceFee,
  couponCode,
  instructions,
  onSuccess
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [hideFromPublic, setHideFromPublic] = useState(true); // Default to hidden - admin can show later
  
  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [discountedFee, setDiscountedFee] = useState(attendanceFee);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    username: '',
    password: '',
    confirmPassword: '',
    collegeName: '',
    division: 'D1'
  });
  
  // Reset coupon state when modal opens/closes or fee changes
  React.useEffect(() => {
    setDiscountedFee(attendanceFee);
    setCouponInput('');
    setCouponApplied(false);
  }, [open, attendanceFee]);

  const divisions = ['D1', 'D2', 'D3', 'JUCO', 'NAIA', 'Other'];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyCoupon = () => {
    if (!couponCode || !couponInput.trim()) return;
    
    if (couponInput.toUpperCase().trim() === couponCode.toUpperCase()) {
      // Coupon valid - make it free (100% off)
      setCouponApplied(true);
      setDiscountedFee(0);
      toast.success('Coupon applied - registration is now free!');
    } else {
      toast.error('Invalid coupon code');
      setCouponApplied(false);
      setDiscountedFee(attendanceFee);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponInput('');
    setCouponApplied(false);
    setDiscountedFee(attendanceFee);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const uploadLogo = async (userId: string): Promise<string | null> => {
    if (!logoFile) return null;
    
    try {
      setUploadingLogo(true);
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `coach-logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('event-coaches')
        .upload(filePath, logoFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('event-coaches')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Logo upload error:', error);
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const validateForm = (): boolean => {
    const requiredFields = ['name', 'email', 'phone', 'collegeName', 'username', 'password', 'confirmPassword'];
    
    for (const field of requiredFields) {
      if (!formData[field as keyof typeof formData]) {
        const readableField = field.replace(/([A-Z])/g, ' $1').toLowerCase();
        toast.error(`${readableField} is required`);
        return false;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate .edu email for college coaches
    if (!formData.email.endsWith('.edu')) {
      toast.error('College coaches must use a .edu email address');
      return false;
    }

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      // 1. Create auth user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
            username: formData.username,
            role: 'college_coach'
          }
        }
      });

      if (authError) {
        // Check if user already exists
        if (authError.message.includes('already registered')) {
          toast.error('An account with this email already exists. Please sign in instead.');
        } else {
          toast.error(authError.message);
        }
        return;
      }

      if (!authData.user) {
        toast.error('Failed to create user account');
        return;
      }

      // Wait a moment for the session to propagate
      await new Promise(resolve => setTimeout(resolve, 500));

      // Get the current session to ensure we're authenticated
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        // If no session (email confirmation required), we need to handle this differently
        console.log('No active session - email confirmation may be required');
        // Still try to proceed - RLS policies allow authenticated users
      }

      // 2. Update profile with coach-specific fields
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          state: formData.state,
          username: formData.username,
          role: 'college_coach',
          primary_role_slug: 'college_coach',
          active_membership_slug: 'college_coach',
          college_name: formData.collegeName,
          status: 'active',
          profile_completion: 75
        })
        .eq('user_id', authData.user.id);

      if (profileError) {
        console.error('Profile update error:', profileError);
        // Don't return, continue with event registration
      }

      // 3. Upload logo if provided
      const logoUrl = await uploadLogo(authData.user.id);

      // 4. Create event_coaches record with full pricing metadata
      const { data: coachRecord, error: coachError } = await supabase
        .from('event_coaches')
        .insert({
          event_id: eventId,
          coach_name: formData.name,
          college_name: formData.collegeName,
          division: formData.division,
          coach_email: formData.email,
          coach_phone: formData.phone,
          profile_id: authData.user.id,
          payment_status: discountedFee > 0 ? 'pending' : 'free',
          logo_url: logoUrl,
          hide_from_public: hideFromPublic,
          metadata: {
            coupon_applied: couponApplied ? couponInput.toUpperCase() : null,
            // Pricing fields for merge tags
            registration_amount: attendanceFee,
            discount_amount: couponApplied ? (attendanceFee - discountedFee) : 0,
            total_amount: discountedFee,
            amount_paid: 0,
            amount_owed: discountedFee,
            team_name: formData.collegeName
          }
        })
        .select()
        .single();

      if (coachError) {
        console.error('Event coach creation error:', coachError);
        toast.error('Failed to register for event. Please try again or contact support.');
        return;
      }

      // 5. If there's a fee (after discount), redirect to Stripe checkout
      if (discountedFee > 0) {
        const { data: checkoutData, error: checkoutError } = await invokeEdgeFunction('create-coach-attendance-checkout', {
          body: {
            eventId,
            eventCoachId: coachRecord.id,
            amount: discountedFee,
            coachEmail: formData.email,
            coachName: formData.name,
            collegeName: formData.collegeName
          }
        });

        if (checkoutError || !checkoutData?.url) {
          console.error('Checkout creation error:', checkoutError);
          toast.error('Failed to create payment session');
          return;
        }

        // Redirect to Stripe
        window.location.href = checkoutData.url;
        return;
      }

      // 6. If free, just send notification and sync to Google Sheets
      await invokeEdgeFunction('coach-attendance-notification', {
        body: {
          eventId,
          eventCoachId: coachRecord.id,
          coachName: formData.name,
          coachEmail: formData.email,
          collegeName: formData.collegeName,
          division: formData.division,
          paymentStatus: 'free'
        }
      });

      toast.success('Successfully registered for the event!');
      onSuccess?.();
      onOpenChange(false);
      // Redirect to confirmation page
      navigate(`/coach-registration-confirmation?id=${coachRecord.id}&event_id=${eventId}`);

    } catch (error) {
      console.error('Registration error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Register for {eventName}
          </DialogTitle>
          <DialogDescription>
            Create your college coach account to register for this event
            {attendanceFee > 0 && (
              <span className="block mt-1 font-semibold text-primary">
                Attendance Fee: ${(attendanceFee / 100).toFixed(2)}
                {couponApplied && discountedFee === 0 && (
                  <span className="ml-2 text-green-600">(FREE with coupon)</span>
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Instructions Section - Show if provided */}
          {instructions && (
            <Card className="bg-muted/50 border-primary/20">
              <CardContent className="pt-4">
                <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap">
                  {instructions}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Coupon Code Section - only show if there's a fee and coupon is configured */}
          {attendanceFee > 0 && couponCode && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg border">
              <Label htmlFor="coupon">Coupon Code</Label>
              <div className="flex gap-2">
                <Input
                  id="coupon"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  disabled={couponApplied}
                  className="flex-1"
                />
                {couponApplied ? (
                  <Button type="button" variant="outline" onClick={handleRemoveCoupon}>
                    Remove
                  </Button>
                ) : (
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={handleApplyCoupon}
                    disabled={!couponInput.trim()}
                  >
                    Apply
                  </Button>
                )}
              </div>
              {couponApplied && (
                <p className="text-sm text-green-600 font-medium">
                  ✓ Coupon applied - Registration is free!
                </p>
              )}
            </div>
          )}
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email * <span className="text-sm text-muted-foreground">(must end in .edu)</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="coach@university.edu"
              required
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              formatPhone={true}
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="xxx-xxx-xxxx"
              required
            />
          </div>

          {/* City & State */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select value={formData.state} onValueChange={(v) => handleInputChange('state', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* College Name */}
          <div className="space-y-2">
            <Label htmlFor="collegeName">College/University Name *</Label>
            <Input
              id="collegeName"
              value={formData.collegeName}
              onChange={(e) => handleInputChange('collegeName', e.target.value)}
              placeholder="Enter your college name"
              required
            />
          </div>

          {/* Division */}
          {/* College Logo Upload */}
          <div className="space-y-2">
            <Label>College Logo (optional)</Label>
            {logoPreview ? (
              <div className="relative w-24 h-24 mx-auto">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-full h-full object-contain rounded border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6"
                  onClick={removeLogo}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex justify-center">
                <label className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Upload</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Division */}
          <div className="space-y-2">
            <Label htmlFor="division">Division</Label>
            <Select value={formData.division} onValueChange={(v) => handleInputChange('division', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {divisions.map((div) => (
                  <SelectItem key={div} value={div}>{div}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username *</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              placeholder="Choose a unique username"
              required
            />
          </div>

          {/* Hide from public checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="hideFromPublic"
              checked={hideFromPublic}
              onCheckedChange={(checked) => setHideFromPublic(checked === true)}
            />
            <Label htmlFor="hideFromPublic" className="text-sm font-normal cursor-pointer">
              Hide my attendance from the public event page
            </Label>
          </div>

          {/* Password Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Min 8 characters"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Confirm password"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : discountedFee > 0 ? (
              `Register & Pay $${(discountedFee / 100).toFixed(2)}`
            ) : (
              'Register for Event'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CoachAttendanceModal;